import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useViewMode } from '../../context/ViewModeContext';

interface EntityModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Footer with action buttons */
  footer?: ReactNode;
  /** Max width on desktop (default: max-w-2xl) */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /**
   * 'responsive' (default): full-screen slide-up on mobile, centered dialog on desktop.
   * 'centered': always a centered dialog — for small dialogs (confirm/alert) that
   * should never go full-screen.
   */
  presentation?: 'responsive' | 'centered';
  /** Clicking the backdrop closes the modal (default true) */
  closeOnBackdrop?: boolean;
  /** Pressing Escape closes the modal (default true) */
  closeOnEscape?: boolean;
  /** Show the X close button in the header (default true) */
  showCloseButton?: boolean;
  /** Extra classes for the overlay (e.g. a higher z-index for stacked dialogs) */
  overlayClassName?: string;
}

const SIZE_MAP = {
  xs: 'max-w-sm',
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
};

// Stack of currently-open modals (in mount order). Stacked modals — e.g. a
// MessageDialog confirm on top of an entity modal — must share one body scroll
// lock and route Escape only to the top-most instance.
const openModalStack: symbol[] = [];

export function EntityModal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  presentation = 'responsive',
  closeOnBackdrop = true,
  closeOnEscape = true,
  showCloseButton = true,
  overlayClassName = '',
}: EntityModalProps) {
  const { isMobile } = useViewMode();
  const overlayRef = useRef<HTMLDivElement>(null);
  const modalIdRef = useRef<symbol | null>(null);
  if (modalIdRef.current === null) {
    modalIdRef.current = Symbol('entity-modal');
  }

  // Register on the open-modal stack and hold the shared body scroll lock —
  // released only when the last open modal closes, so dismissing a stacked
  // dialog doesn't unlock scrolling under a still-open outer modal.
  useEffect(() => {
    if (!open) return;
    const modalId = modalIdRef.current as symbol;
    openModalStack.push(modalId);
    document.body.style.overflow = 'hidden';
    return () => {
      const index = openModalStack.indexOf(modalId);
      if (index !== -1) openModalStack.splice(index, 1);
      if (openModalStack.length === 0) {
        document.body.style.overflow = '';
      }
    };
  }, [open]);

  // Close on Escape — only the top-most open modal reacts, so Escape on a
  // stacked dialog never also closes the modal underneath it.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (openModalStack[openModalStack.length - 1] !== modalIdRef.current) return;
      if (closeOnEscape) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, closeOnEscape, onClose]);

  if (!open) return null;

  // Rendered through a portal so a modal opened from inside another modal's
  // scrollable body is a direct child of <body>, never a descendant of an
  // `overflow-auto` container under a `backdrop-filter` overlay — that nesting
  // breaks compositing/hit-testing in some browsers (Chrome on macOS).
  if (isMobile && presentation === 'responsive') {
    // Full-screen slide-up on mobile
    return createPortal(
      <div
        className="fixed inset-0 z-50 flex flex-col bg-white animate-in slide-in-from-bottom"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white sticky top-0 z-10">
          <h2 className="text-lg font-semibold text-gray-900 truncate">{title}</h2>
          {showCloseButton && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="p-2 -mr-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          )}
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-auto p-4">{children}</div>

        {/* Sticky footer */}
        {footer && (
          <div className="border-t border-gray-200 px-4 py-3 bg-white sticky bottom-0">
            {footer}
          </div>
        )}
      </div>,
      document.body,
    );
  }

  // Desktop (or forced-centered): centered dialog with backdrop
  return createPortal(
    <div
      ref={overlayRef}
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm ${overlayClassName}`}
      onClick={(e) => {
        e.stopPropagation();
        if (closeOnBackdrop && e.target === overlayRef.current) {
          onClose();
        }
      }}
    >
      <div
        className={`bg-white rounded-xl shadow-2xl w-full ${SIZE_MAP[size]} flex flex-col max-h-[85vh] animate-in zoom-in-95 fade-in duration-200`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          {showCloseButton && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          )}
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-auto px-6 py-4">{children}</div>

        {/* Footer */}
        {footer && (
          <div className="border-t border-gray-100 px-6 py-4 flex justify-end gap-3">{footer}</div>
        )}
      </div>
    </div>,
    document.body,
  );
}
